import { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { LoadingState } from '@leanin/ui';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { RecoveryError, type RecoveryReason } from '@/components/auth/RecoveryError';
import { getApiClient } from '@/lib/api-client';
import { parseApiErrorCode, parseApiErrorDetails } from '@/lib/api-error';
import { sessionQuery } from '@/lib/use-session';
import { clearSignupRef, readSignupRef } from '@/lib/signup-ref-cookie';

// `next` is the post-onboarding destination set by upstream flows that
// know where the user should land — today that's `createInviteMagicLink`,
// which builds the invited group's URL and threads it through Clerk's
// invitation redirect so first-time signups land in the group rather than
// the generic /feed. Validated as an in-app absolute path so a leaked
// invite URL can't route to an external host post-signup.
const finishingSearchSchema = z.object({
  next: z
    .string()
    .min(1)
    .refine((v) => v.startsWith('/') && !v.startsWith('//'), {
      message: 'must_be_in_app_path',
    })
    .optional(),
});

export const Route = createFileRoute('/auth/finishing')({
  validateSearch: finishingSearchSchema.parse,
  component: FinishingPage,
});

// Post-signup landing for both OAuth and email/password sign-ups. Calls
// POST /api/v1/auth/finish-signup and routes the user based on the server's
// onboarding completion signal:
//   - `needs_onboarding` → send to /onboarding/profile
//   - `onboarded` → send straight to /feed
//
// Reading `users.onboarding_completed_at` (not "did we just insert the
// row?") is what closes the SSO race: the Clerk `user.created` webhook
// frequently lands before this page calls finish-signup, so the legacy
// "row already exists ⇒ they're done" check shipped SSO sign-ups to /feed
// before they ever saw onboarding.
//
// This endpoint uses Clerk's `authenticateRequest` directly, which ignores
// dev-bypass — so a dev-bypass user reaching this page can't proceed.

// Bound the wait for Clerk session hydration before surfacing a recovery
// surface. A null `user` immediately after `isLoaded` flips is normal
// (Clerk reports loaded as soon as the SDK initializes; the session
// itself can take another tick to propagate from setActive). Bouncing
// instantly on null sends a freshly-signed-up visitor through the root
// index → /auth/login dead-end. 5s is a generous upper bound — typical
// hydration takes < 200ms; anything longer is a real failure mode that
// deserves the recovery surface, not a silent bounce.
const SESSION_HYDRATION_MAX_WAIT_MS = 5_000;

function FinishingPage() {
  const t = useTranslations('Auth');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Use `useAuth()` over `useUser()` for the post-signup hydration gate:
  // `useAuth().isLoaded` flips once Clerk's SDK has fully initialised and
  // resolved the active session (`sessionId`). `useUser().user` lags a tick
  // — it hydrates from the session in a SECOND React render, so reading
  // `user === null` immediately after `setActive()` raced the cookie
  // propagation and we bounced to `/` mid-signup. The 5s timer below
  // becomes a defensive backstop, not the primary gate.
  const { isLoaded, isSignedIn, sessionId } = useAuth();
  const { next } = Route.useSearch();
  const [recoveryReason, setRecoveryReason] = useState<RecoveryReason | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const client = getApiClient();
      // Invite redemption is server-driven by the verified email
      // (redeemInvitesForEmail) — nothing to pass from the client.
      const { data, error } = await client.POST('/api/v1/auth/finish-signup', {});
      if (error !== undefined || data === undefined) {
        const code = parseApiErrorCode(error);
        const details = parseApiErrorDetails(error);
        let reason: RecoveryReason = 'unknown';
        if (code === 'VALIDATION' && details?.user?.includes('no_email_address')) {
          reason = 'no_email_address';
        } else if (code === 'identity/email_in_use') {
          reason = 'email_in_use';
        } else if (code === 'identity/clerk_unavailable') {
          reason = 'clerk_unavailable';
        }
        throw { kind: 'recovery', reason } as const;
      }
      return data;
    },
    onSuccess: async (data) => {
      // The unauthenticated visit to /auth/{login,signup,password-reset}
      // cached a `null` session (those routes run `redirectIfSignedIn`).
      // Now that Clerk has an active session and the member row exists, we
      // need the next route guard's `ensureQueryData(sessionQuery)` call to
      // see the FRESH /me/context — otherwise it reads the cached null and
      // bounces straight back to /auth/login.
      //
      // `refetchQueries` invalidates AND awaits the in-flight fetch, unlike
      // `invalidateQueries` which only marks stale and lets the next reader
      // race the new Clerk session-cookie's propagation. The race
      // manifests as a 401 from /me/context → null context → _authed
      // bounces to /auth/login. Awaiting the refetch here closes the race.
      await queryClient.refetchQueries({ queryKey: sessionQuery.queryKey });
      // Best-effort referral attribution. The session is live and the member
      // row exists, so the claim authenticates. Stamping a code grants nothing,
      // so a failure must never block or fail the finishing flow — claim once,
      // then clear the cookie regardless of the result.
      await claimSignupRef();
      if (data.status === 'onboarded') {
        // `next` overrides the default /feed landing when an upstream flow
        // already knows where the user should land (today: invited
        // signups → the group page). Validated upstream as an in-app
        // absolute path; `replace: true` so /auth/finishing doesn't sit
        // in browser history.
        if (next !== undefined) {
          window.location.replace(next);
        } else {
          void navigate({ to: '/feed' });
        }
        return;
      }
      // needs_onboarding: route through onboarding, which already
      // resolves the invited group server-side via /me/onboarding-progress
      // (leaderCircleId / leaderNetworkId) — we don't need to thread
      // `next` through onboarding ourselves.
      void navigate({ to: '/onboarding/profile' });
    },
    onError: (err: unknown) => {
      const recovery = err as { kind?: string; reason?: RecoveryReason };
      if (recovery.kind === 'recovery' && recovery.reason !== undefined) {
        setRecoveryReason(recovery.reason);
      } else {
        setRecoveryReason('unknown');
      }
    },
  });

  // Bounded wait for Clerk session hydration. `useAuth().isLoaded`
  // flips once Clerk's SDK has initialised; `sessionId` populates as
  // soon as `setActive()` completes propagation. The combo
  // (isLoaded && isSignedIn && sessionId) is the canonical "session
  // is live" gate per Clerk's React hook docs. Reading `useUser().user`
  // lags this by a tick — that's how we shipped the original race
  // (bouncing to `/` while `user` was still null).
  //
  // After SESSION_HYDRATION_MAX_WAIT_MS without a live session, surface
  // RecoveryError with the 'unknown' reason instead of bouncing silently.
  const sessionLive =
    isLoaded && isSignedIn === true && sessionId !== null && sessionId !== undefined;
  useEffect(() => {
    if (!isLoaded) return;
    if (sessionLive) {
      if (mutation.isIdle) mutation.mutate();
      return;
    }
    const timer = setTimeout(() => {
      // Re-check inside the timer in case the session populated between
      // setTimeout schedule and fire (React batches effects).
      setRecoveryReason((prev) => prev ?? 'unknown');
    }, SESSION_HYDRATION_MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isLoaded, sessionLive, mutation, navigate]);

  if (recoveryReason !== null) {
    return <RecoveryError reason={recoveryReason} />;
  }

  return (
    <AuthLayout kicker={t('finishing.kicker')} headline={t('finishing.headline')}>
      <LoadingState placement="page" label={t('finishing.label')} />
    </AuthLayout>
  );
}

// Reads the `signup_ref` cookie (set on the signup route from `?ref=<code>`)
// and posts the attribution claim once, then deletes the cookie regardless of
// outcome. Best-effort: the code grants nothing, so any error is swallowed —
// this must never block or fail the finishing flow.
async function claimSignupRef(): Promise<void> {
  const code = readSignupRef();
  if (code === null) return;
  try {
    const client = getApiClient();
    await client.POST('/api/v1/signup-attribution/claim', { body: { code } });
  } catch {
    // Attribution is non-essential — swallow and continue.
  } finally {
    clearSignupRef();
  }
}
